import { CreditCard, Trash2 } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useDeleteBillingKey } from '@/hooks/admin/useContentFee'
import type { BillingKeyResponse } from '@/api/contentFee'

interface Props {
  billingKeys: BillingKeyResponse[]
  selectedBillingKeyId: number | null
  onSelectBillingKey: (id: number) => void
  isLoading?: boolean
  disabled?: boolean
  canDelete?: boolean
}

export function BillingKeySelector({
  billingKeys,
  selectedBillingKeyId,
  onSelectBillingKey,
  isLoading = false,
  disabled = false,
  canDelete = true,
}: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const deleteMutation = useDeleteBillingKey()

  const handleDelete = (id: number) => {
    if (!window.confirm(t('admin:students.detail.contentFee.billingKeys.deleteConfirm'))) return
    deleteMutation.mutate(id)
  }

  if (isLoading) {
    return (
      <p className="text-sm text-gray-500">
        {t('admin:students.detail.contentFee.billingKeys.loading')}
      </p>
    )
  }

  if (billingKeys.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500">
        {t('admin:students.detail.contentFee.billingKeys.empty')}
      </p>
    )
  }

  return (
    <div role="radiogroup" aria-label={t('admin:students.detail.contentFee.billingKeys.label')} className="space-y-2">
      {billingKeys.map((card) => {
        const isCardDisabled = disabled || !card.usable
        const label = [
          card.cardAlias,
          card.cardCompanyName,
          card.cardLast4 ? `**** ${card.cardLast4}` : null,
        ].filter(Boolean).join(' ')

        return (
          <div
            key={card.id}
            className={clsx(
              'flex items-center gap-3 rounded-lg border px-3 py-3',
              selectedBillingKeyId === card.id
                ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                : 'border-gray-200',
              isCardDisabled && 'bg-gray-50 text-gray-500',
            )}
          >
            <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
              <input
                type="radio"
                name="billing-key"
                checked={selectedBillingKeyId === card.id}
                disabled={isCardDisabled}
                onChange={() => onSelectBillingKey(card.id)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500"
              />
              <CreditCard className="h-5 w-5 shrink-0 text-gray-500" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-gray-900">{label}</span>
                <span className="block text-xs text-gray-500">
                  {card.provider.toUpperCase()} · {card.providerMode}
                  {!card.usable && (
                    <>
                      {' · '}
                      {t('admin:students.detail.contentFee.billingKeys.modeMismatch')}
                    </>
                  )}
                </span>
              </span>
            </label>
            {canDelete ? (
              <button
                type="button"
                onClick={() => handleDelete(card.id)}
                disabled={disabled || deleteMutation.isPending}
                className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                {t('admin:students.detail.contentFee.billingKeys.delete')}
              </button>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
